import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, ExternalLink, FileCheck2, Loader2, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { Button } from "@/components/ui/button";
import ProjectTabs from "@/components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const euros = (value) => new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
}).format(Number(value || 0));
const dateFr = (value) => value ? new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "long", timeStyle: "short",
}).format(new Date(value)) : "";

export default function ProjectCommercialPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/commercial`, auth)
      .then(({ data: result }) => setData(result))
      .catch(() => toast.error("Impossible de charger le devis et le CDC"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const download = async () => {
    const doc = data?.latest_document;
    if (!doc) return;
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}`, {
        ...auth, responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename || "Devis_CDC.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Le téléchargement a échoué");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div></CRMLayout>
  );

  const lines = data?.devis?.lines || [];
  const totals = data?.totals || {};
  const doc = data?.latest_document;

  return (
    <CRMLayout>
      <div className="p-5 lg:p-8 max-w-6xl">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <ProjectTabs projectId={projectId} actif="commercial" />

        {!data?.linked ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            Ce projet n’est pas encore relié à une fiche commerciale.
          </div>
        ) : <>
          <div className="rounded-2xl bg-gradient-to-br from-slate-950 to-indigo-950 text-white p-6 lg:p-8 mb-5 shadow-lg">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div>
                <div className="text-indigo-200 text-xs font-bold tracking-widest uppercase mb-2">Document client · {data.opportunity.reference}</div>
                <h1 className="text-2xl lg:text-3xl font-bold">Devis & cahier des charges</h1>
                <p className="text-slate-300 mt-2">{data.opportunity.company_name} · {data.project.name}</p>
              </div>
              <div className="flex gap-7">
                <div><div className="text-xs text-slate-400">Total HT</div><div className="text-xl font-bold">{euros(totals.net_ht)}</div></div>
                <div><div className="text-xs text-slate-400">Total TTC</div><div className="text-2xl font-bold text-emerald-300">{euros(totals.ttc)}</div></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center gap-3">
                <ReceiptText className="w-5 h-5 text-indigo-600" />
                <div><h2 className="font-bold text-slate-900">Détail du devis</h2><p className="text-xs text-slate-500">{lines.length} prestation{lines.length > 1 ? "s" : ""}</p></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                    <th className="text-left px-5 py-3">Prestation</th><th className="text-right px-3 py-3">Qté</th>
                    <th className="text-right px-3 py-3">Prix unitaire</th><th className="text-right px-5 py-3">Total HT</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {lines.map((line, index) => <tr key={index} className="align-top">
                      <td className="px-5 py-4"><div className="font-semibold text-slate-900">{line.label}</div>{line.description && <div className="text-xs text-slate-500 mt-1">{line.description}</div>}</td>
                      <td className="px-3 py-4 text-right whitespace-nowrap">{line.quantity} {line.unit}</td>
                      <td className="px-3 py-4 text-right whitespace-nowrap">{euros(line.unit_price)}</td>
                      <td className="px-5 py-4 text-right font-semibold whitespace-nowrap">{euros(Number(line.quantity) * Number(line.unit_price))}</td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 flex justify-end">
                <div className="w-full sm:w-72 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Total HT</span><span>{euros(totals.net_ht)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">TVA {totals.taux_tva}%</span><span>{euros(totals.tva)}</span></div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-200"><span>Total TTC</span><span>{euros(totals.ttc)}</span></div>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="bg-white border border-emerald-200 rounded-2xl shadow-sm p-5">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4"><FileCheck2 className="w-6 h-6" /></div>
                <h2 className="font-bold text-slate-900">Document à transmettre</h2>
                {doc ? <>
                  <p className="text-sm text-slate-600 mt-2">{doc.title}</p>
                  <p className="text-xs text-slate-400 mt-1">Créé le {dateFr(doc.created_at)}</p>
                  <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2.5 mt-3">Le devis apparaît en premier, suivi du cahier des charges complet.</p>
                  <Button onClick={download} disabled={downloading} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700">
                    {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Télécharger le PDF client
                  </Button>
                </> : <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mt-3">Aucun PDF client n’a encore été archivé.</p>}
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
                <h3 className="font-bold text-slate-900">Conditions</h3>
                <dl className="text-sm mt-3 space-y-3">
                  <div><dt className="text-xs text-slate-400">Validité</dt><dd>{data.devis.validity_days || 30} jours</dd></div>
                  {data.devis.delay && <div><dt className="text-xs text-slate-400">Délai prévisionnel</dt><dd>{data.devis.delay}</dd></div>}
                  {data.devis.payment_terms && <div><dt className="text-xs text-slate-400">Paiement</dt><dd>{data.devis.payment_terms}</dd></div>}
                </dl>
                <Link to={`/dashboard/opportunities/${data.opportunity.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 mt-5">
                  Modifier le devis et le CDC <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </>}
      </div>
    </CRMLayout>
  );
}
